import React, { useState, useEffect, useRef } from 'react';
import { ChatService, ChatMessage, ChatContext } from '../../services/chatService';
import { MessageCircle, X, Send, Loader, Bot, User, Minimize2 } from 'lucide-react';

interface ChatWidgetProps {
  chatContext: ChatContext;
  isVisible?: boolean;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ 
  chatContext, 
  isVisible = true 
}) => {
  const [chatService] = useState(() => new ChatService());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 初始化消息
  useEffect(() => {
    setMessages(chatService.getMessages());
  }, [chatService]);

  // 滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 检查是否需要主动建议
  useEffect(() => {
    if (!isVisible) return;

    const checkContextualSuggestion = async () => {
      try {
        const suggestion = await chatService.getContextualSuggestion(chatContext);
        if (suggestion) {
          const suggestionMessage: ChatMessage = {
            id: 'suggestion_' + Date.now(),
            role: 'assistant',
            content: suggestion,
            timestamp: Date.now()
          };
          setMessages(prev => [...prev, suggestionMessage]);
          if (!isExpanded) {
            setHasNewMessage(true);
          }
        }
      } catch (error) {
        console.error('获取智能建议失败:', error);
      }
    };

    // 防抖：避免频繁检查
    const timeoutId = setTimeout(checkContextualSuggestion, 2000);
    return () => clearTimeout(timeoutId);
  }, [chatContext.timeSpentOnCurrentQuestion, chatContext.userResponses.length, isVisible, isExpanded, chatService]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue(''); // 立即清空输入框

    // 立即显示用户消息
    const userChatMessage: ChatMessage = {
      id: 'user_' + Date.now(),
      role: 'user',
      content: userMessage,
      timestamp: Date.now(),
      context: {
        currentQuestion: chatContext.currentQuestion || undefined,
        userPerformance: calculateUserPerformance(chatContext)
      }
    };
    
    setMessages(prev => [...prev, userChatMessage]);
    setIsLoading(true);

    try {
      // 发送到服务并获取AI回复
      await chatService.sendMessage(userMessage, chatContext);
      // 更新所有消息（包括AI回复）
      setMessages(chatService.getMessages());
      
      // 自动展开显示回复
      if (!isExpanded) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('发送消息失败:', error);
      // 发生错误时添加错误消息
      const errorMessage: ChatMessage = {
        id: 'error_' + Date.now(),
        role: 'assistant',
        content: '抱歉，我暂时无法回复。请稍后再试。',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // 计算用户表现的辅助函数
  const calculateUserPerformance = (context: ChatContext): {
    accuracy: number;
    speed: number;
    recentErrors: string[];
  } => {
    const responses = context.userResponses;
    if (responses.length === 0) {
      return {
        accuracy: 0.5,
        speed: 1.0,
        recentErrors: []
      };
    }

    const correctAnswers = responses.filter(r => r.is_correct).length;
    const accuracy = correctAnswers / responses.length;

    let totalSpeedRatio = 0;
    let validSpeedCount = 0;
    const recentErrors: string[] = [];
    
    responses.forEach((response, index) => {
      const question = context.previousQuestions[index];
      if (question && response) {
        const timeLimit = question.time_limit || 60;
        const speedRatio = response.time_spent / (timeLimit * 1000);
        totalSpeedRatio += Math.min(speedRatio, 2);
        validSpeedCount++;

        if (!response.is_correct) {
          recentErrors.push(question.content.title.slice(0, 20) + '...');
        }
      }
    });

    const speed = validSpeedCount > 0 ? totalSpeedRatio / validSpeedCount : 1.0;

    return {
      accuracy,
      speed,
      recentErrors: recentErrors.slice(-3)
    };
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    setHasNewMessage(false);
    
    // 展开时聚焦到输入框
    if (!isExpanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* 聊天窗口 */}
      <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 transition-all duration-300 ${
        isExpanded ? 'w-96 h-96' : 'w-0 h-0 overflow-hidden'
      }`}>
        {isExpanded && (
          <>
            {/* 头部 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI学习伴侣</h3>
                  <p className="text-xs text-gray-600">随时为你答疑解惑</p>
                </div>
              </div>
              <button
                onClick={toggleExpanded}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Minimize2 className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            {/* 消息列表 */}
            <div className="flex-1 p-4 h-64 overflow-y-auto space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-900 rounded-bl-md'
                  }`}>
                    <div className="flex items-start space-x-2">
                      {message.role === 'assistant' && (
                        <Bot className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                      {message.role === 'user' && (
                        <User className="h-4 w-4 mt-0.5 text-blue-200 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* 正在输入指示器 */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-bl-md px-4 py-2">
                    <div className="flex items-center space-x-2">
                      <Bot className="h-4 w-4 text-blue-500" />
                      <Loader className="h-4 w-4 animate-spin text-blue-500" />
                      <span className="text-sm text-gray-600">AI正在思考...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* 输入框 */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="和AI聊聊学习问题..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 聊天按钮 */}
      <div className="mt-4 flex justify-end">
        <button
          onClick={toggleExpanded}
          className={`relative w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
            isExpanded 
              ? 'bg-gray-500 hover:bg-gray-600' 
              : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 animate-pulse'
          }`}
        >
          {isExpanded ? (
            <X className="h-6 w-6 text-white" />
          ) : (
            <>
              <MessageCircle className="h-6 w-6 text-white" />
              {hasNewMessage && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                </div>
              )}
            </>
          )}
        </button>
      </div>

      {/* 提示气泡 */}
      {!isExpanded && messages.length <= 1 && (
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-lg p-3 border border-gray-200 max-w-64 animate-bounce">
          <div className="text-sm text-gray-700">
            <Bot className="h-4 w-4 inline mr-1 text-blue-500" />
            有问题随时问我！点击开始对话
          </div>
          <div className="absolute bottom-0 right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"></div>
        </div>
      )}
    </div>
  );
};